/* eslint-disable new-cap */
import { ControllerNodeBase, ControllerNodeOptions, Publish } from '@rta/rta-blueprint-library';

import { Kitchen3dController as ComponentController } from './kitchen-3d-controller.class';
import { DesignerOptionsDataIn } from './types/designer-options-data.type';
import { designerOptionsDefaults } from './defaults/designer-options-defaults';

export class DesignerOptions extends ControllerNodeBase<DesignerOptionsDataIn> {
	#zoomIntervalTimer: ReturnType<typeof setTimeout> | null;
	#controller: ComponentController;

	// performance tracking
	public fps: Record<string, { fps: number; count: number; avg: number; }>;
	public cursorMode: unknown;
	public contextMenu: boolean;
	public useFractions: boolean;
	public backgroundColor: string;
	public borderColor: string;
	public cutoutColor: string;
	public highlightFrontPanel: boolean;
	public showControls: boolean;

	#applianceOpacity: number;
	#cutoutOpacity: number;
	#readonly: boolean;

	#showAllDimensions: boolean;
	#showAppliances: boolean;
	#showApplianceDimensions: boolean;
	#showBreakpoints: boolean;
	#showCutouts: boolean;
	#showCountertops: boolean;
	#showCorners: boolean;
	#showDeveloperInsights: boolean;
	#showDimensions: boolean;
	// #showDimensionsMode: ShowDimensions;
	#showHints: boolean;
	#showIslandBaseDimensions: boolean;
	#showTitle: boolean;
	#showOverlap: boolean;
	#showOverheadDimensions: boolean;
	#showPanelInsideDimensions: boolean;
	#showPanelOutsideDimensions: boolean;
	#debugCutouts: boolean;

	public showDisplayMode: boolean;
	public showZoom: boolean;

	public assetsBaseUrl: string | undefined;
	public assetsSasToken: string | undefined;
	public panelBottomPadding: number;

	public corner: {
		show: boolean;
		width: number;
		thickness: number;
	};

	// public coordinateSystem: {
	// 	canvas: {
	// 		width: number;
	// 		height: number;
	// 	};
	// 	viewport: {
	// 		xOffset: number;
	// 		yOffset: number;
	// 	};
	// 	margin: {
	// 		top: number;
	// 		bottom: number;
	// 		left: number;
	// 		right: number;
	// 	};
	// };

	readonly appliance: {
		fontSizeFactor: number;
		showLabel: boolean;
	};

	public dimensions: {
		fontSizeFactor: number;
	};

	// Appliance Groups
	public showApplianceGroups: boolean;

	// Rotation arc visualizations after scene build (data-manager preview only)
	public showRotationArcs: boolean;

	// Render Layer Configuration
	// public renderLayerConfig: RenderLayerConfig;

	// Viewport Configuration - centralized viewport settings
	// public viewport: {
	// 	// Zoom settings
	// 	zoomFactor: number | null;
	// 	autoZoom: boolean | null;
	// 	autoZoomOverhead: boolean;

	// 	// Pan/Offset settings
	// 	xOffset: number;
	// 	yOffset: number;

	// 	// Scale settings
	// 	xScale: number;
	// 	yScale: number;
	// 	xScaleFactor: number;
	// 	yScaleFactor: number;

	// 	// Rotation settings
	// 	rotation: number;

	// 	// Animation settings
	// 	smoothZoomEnabled: boolean;
	// 	smoothZoomDuration: number;
	// 	smoothZoomEasing: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

	// 	// Viewport behavior
	// 	enableWheelZoom: boolean;
	// 	wheelZoomSensitivity: number;
	// 	enablePan: boolean;
	// 	panSensitivity: number;

	// 	// Constraints
	// 	minZoom: number;
	// 	maxZoom: number;
	// 	zoomSnap: number;
	// };

	constructor (options: ControllerNodeOptions<DesignerOptionsDataIn>, controller: ComponentController) {
		super('designer-options', options);
		const initOptions = {
			...designerOptionsDefaults,
			...(options.dataIn ?? {})
		};

		this.#controller = controller;

		// all options should be public
		Object.keys(initOptions)
			.forEach((key) => {
				this[key] = initOptions[key];
			});

		this.setInitialized();
	}

	@Publish('designer_options_readonly', 'updated')
	set readonly (readonly: boolean) { this.#readonly = readonly; }
	get readonly (): boolean { return this.#readonly; }

	@Publish('layer-option-appliance-opacity', 'updated')
	set applianceOpacity (opacity: number) { this.#applianceOpacity = opacity; }
	get applianceOpacity (): number { return this.#applianceOpacity; }

	@Publish('layer-option-cutout-opacity', 'updated')
	set cutoutOpacity (opacity: number) { this.#cutoutOpacity = opacity; }
	get cutoutOpacity (): number { return this.#cutoutOpacity; }

	@Publish('designer-options-show-all-dimensions', 'updated')
	set showAllDimensions (show: boolean) { this.#showAllDimensions = show; }
	get showAllDimensions (): boolean { return this.#showAllDimensions; }

	@Publish('layer-option-appliance', 'updated')
	set showAppliances (show: boolean) { this.#showAppliances = show; }
	get showAppliances (): boolean { return this.#showAppliances; }

	@Publish('layer-option-appliance-dimensions', 'updated')
	set showApplianceDimensions (show: boolean) { this.#showApplianceDimensions = show; }
	get showApplianceDimensions (): boolean { return this.#showApplianceDimensions; }

	@Publish('layer-option-appliance-label', 'updated')
	set showApplianceLabels (show: boolean) { this.appliance.showLabel = show; }
	get showApplianceLabels (): boolean { return this.appliance.showLabel; }

	@Publish('layer-option-breakpoint', 'updated')
	set showBreakpoints (show: boolean) { this.#showBreakpoints = show; }
	get showBreakpoints (): boolean { return this.#showBreakpoints; }

	@Publish('layer-option-cutout', 'updated')
	set showCutouts (show: boolean) { this.#showCutouts = show; }
	get showCutouts (): boolean { return this.#showCutouts; }

	@Publish('layer-option-countertop', 'updated')
	set showCountertops (show: boolean) { this.#showCountertops = show; }
	get showCountertops (): boolean { return this.#showCountertops; }

	@Publish('layer-option-corner', 'updated')
	set showCorners (show: boolean) { this.#showCorners = show; }
	get showCorners (): boolean { return this.#showCorners; }

	@Publish('layer-option-developer-insight', 'updated')
	set showDeveloperInsights (show: boolean) { this.#showDeveloperInsights = show; }
	get showDeveloperInsights (): boolean { return this.#showDeveloperInsights; }

	@Publish('layer-option-dimensions', 'updated')
	set showDimensions (show: boolean) { this.#showDimensions = show; }
	get showDimensions (): boolean { return this.#showDimensions; }

	@Publish('layer-option-hint', 'updated')
	set showHints (show: boolean) { this.#showHints = show; }
	get showHints (): boolean { return this.#showHints; }

	@Publish('layer-option-island-base-dimension', 'updated')
	set showIslandBaseDimensions (show: boolean) { this.#showIslandBaseDimensions = show; }
	get showIslandBaseDimensions (): boolean { return this.#showIslandBaseDimensions; }

	@Publish('layer-option-title', 'updated')
	set showTitle (show: boolean) { this.#showTitle = show; }
	get showTitle (): boolean { return this.#showTitle; }

	@Publish('layer-option-overlap', 'updated')
	set showOverlap (show: boolean) { this.#showOverlap = show; }
	get showOverlap (): boolean { return this.#showOverlap; }

	@Publish('layer-option-overhead-dimension', 'updated')
	set showOverheadDimensions (show: boolean) { this.#showOverheadDimensions = show; }
	get showOverheadDimensions (): boolean { return this.#showOverheadDimensions; }

	@Publish('layer-option-panel-inside-dimension', 'updated')
	set showPanelInsideDimensions (show: boolean) { this.#showPanelInsideDimensions = show; }
	get showPanelInsideDimensions (): boolean { return this.#showPanelInsideDimensions; }

	@Publish('layer-option-panel-outside-dimension', 'updated')
	set showPanelOutsideDimensions (show: boolean) { this.#showPanelOutsideDimensions = show; }
	get showPanelOutsideDimensions (): boolean { return this.#showPanelOutsideDimensions; }

	@Publish('layer-option-debug-cutout', 'updated')
	set debugCutouts (show: boolean) { this.#debugCutouts = show; }
	get debugCutouts (): boolean { return this.#debugCutouts; }

	// @Publish('component-option-dimensions-mode', 'updated')
	// set showDimensionsMode (show: ShowDimensions) { this.#showDimensionsMode = show; }
	// get showDimensionsMode (): ShowDimensions { return this.#showDimensionsMode; }
}

